// src/hooks/useCars.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ProductService } from '../admin/pages/car/carService';

export const PRODUCT_KEYS = {
  all:       ['products'] as const,
  lists:     () => ['products', 'list'] as const,
  paginated: (
    page: number,
    search: string,
    category: string,
    status: string,
    branch: string,
    featured: string,
    brand: string,
    condition: string,
  ) => ['products', 'list', page, search, category, status, branch, featured, brand, condition] as const,
  stats:     () => ['products', 'stats'] as const,
};

// FIX: Removed the `cursors` field — seek-based pagination only needs `page`.
export interface ProductPaginationParams {
  page: number;
  search: string;
  categoryFilter: string;
  statusFilter: string;
  branchFilter: string;
  featuredFilter: string;
  brandFilter: string;
  conditionFilter: string;
  pageSize: number;
  // `cursors` intentionally removed — seek-based pagination uses only `page`
}

// FIX: Passes `params.page` directly to getProductsPaginated instead of
// cursor objects. Any page can now be fetched in any order.
export const useProductsPaginated = (params: ProductPaginationParams) => {
  return useQuery({
    queryKey: PRODUCT_KEYS.paginated(
      params.page,
      params.search,
      params.categoryFilter,
      params.statusFilter,
      params.branchFilter,
      params.featuredFilter,
      params.brandFilter,
      params.conditionFilter,
    ),
    queryFn: () =>
      ProductService.getProductsPaginated(
        params.pageSize,
        params.page,
        params.search        || undefined,
        params.categoryFilter !== 'all' ? params.categoryFilter : undefined,
        params.statusFilter   !== 'all' ? params.statusFilter   : undefined,
        params.branchFilter   !== 'all' ? params.branchFilter   : undefined,
        params.featuredFilter !== 'all' ? params.featuredFilter : undefined,
        params.brandFilter    !== 'all' ? params.brandFilter    : undefined,
        params.conditionFilter !== 'all' ? params.conditionFilter : undefined,
      ),
    staleTime: 1000 * 60 * 2,
    placeholderData: (prev: any) => prev,
  });
};

export const useProductStats = () => {
  return useQuery({
    queryKey: PRODUCT_KEYS.stats(),
    queryFn: () => ProductService.getProductStats(),
    staleTime: 1000 * 60 * 3,
  });
};

export const useInvalidateProducts = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: PRODUCT_KEYS.all });
    queryClient.invalidateQueries({ queryKey: PRODUCT_KEYS.stats() });
  };
};

export const useProductMutations = () => {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: PRODUCT_KEYS.all });
  };

  const remove = useMutation({
    mutationFn: ({ productId, userId, userName }: { productId: string; userId?: string; userName?: string }) =>
      ProductService.deleteProduct(productId, userId || '', userName || ''),
    onSuccess: invalidateAll,
  });

  const toggleFeatured = useMutation({
    mutationFn: ({ productId, isFeatured }: { productId: string; isFeatured: boolean }) =>
      ProductService.toggleFeaturedProduct(productId, isFeatured),
    onSuccess: invalidateAll,
  });

  const toggleVisibility = useMutation({
    mutationFn: ({ productId, isVisible }: { productId: string; isVisible: boolean }) =>
      ProductService.toggleVisibility(productId, isVisible),
    onSuccess: invalidateAll,
  });

  return { remove, toggleFeatured, toggleVisibility };
};